import React from 'react';
import Styles, { cancelColor, confirmBorderColor } from '../constants/Styles';

// components stuff
import Loader from '../components/Loader';
import { SENDER_NOTE } from '../components/DRViewUtils';

// sdk stuff
import { TaskStepInteractView } from '../vodjs/TaskStepView';
import TaskRequest from '../vodjs/TaskRequest';
import { generatePinCodes, tmpIncreasePollSpeed } from '../vodjs/PollMgr';
import Locktube, { LOCKTUBE, LOCKTUBE_ACCESS_STATUS } from '../vodjs/Locktube';
import ConfirmLoadScreen from './ConfirmLoadScreen';
import ArrivalScreen from './ArrivalScreen';
import ThinkingScreen from './ThinkingScreen';
import PinCodeScreen from './PinCodeScreen';
import { getLocktubeIds, getRobotId, r2h, countForReload } from '../vodjs/lib';
import { areSame, noteInteraction } from '../vodjs/Utils';
import { composeArrivalLoadText, capitalizeStringArray, GENERAL_PICKUP_TEXT, LOST_CONNECTION_TEXT, joinCommaSeparatedNames } from './utils/composeText';
import { getFilledStorageNames } from './utils/containerManager';
import { BatchTypes } from './utils/taskAttrs';
import { updateTaskData } from '../vodjs/Task';
import { elasticLogEvent, elasticLogTaskAction, elasticLogError } from '../vodjs/ElasticLog';
import LoadItemsScreen, { LOAD_PROMPTS } from './LoadItemsScreen';
import BatchLoadItemsScreen from './BatchLoadItemsScreen';
import { getConfig } from '../utils/configUtils';
import { capitalizeString } from '../utils/textUtils';
import ErrorScreen from './ErrorScreen';
import { getUseFirestore } from '../constants/Meta';


export default class LoadScreen extends TaskStepInteractView {
  constructor(props) {
    super(props);

    let doBatchAssist = false;
    if (this.props.taskAttrs.batchData) {
      let { id, type, index, total } = this.props.taskAttrs.batchData;
      doBatchAssist = !!this.props.batchAssist && type == BatchTypes.oneToMany && index > 0;
      if (type == BatchTypes.oneToMany) this.min1MDrawers = total;
    }

    let ccTaskInfo;
    let curTask = this.props.tasks.find((t) => t.uuid == this.props.taskAttrs.uuid);
    if(curTask?.task_data.ccTask) {
      ccTaskInfo = {
        mode:curTask.task_data.ccTask,
        dropoff: curTask.task_level_args.end_goal.value
      };
    }

    this.state = {
      confirmVisible: false,
      currentDeliveryNote: null,
      isLoading: false,
      loaderVisible: false,
      showSuccess: false,
      tubesUnlocked: false,
      tubesFilled: false,
      locktubes: Locktube.defaultLocktubes(),
      arrivalMode: 0, // 0 = waiting for signal to show Continue button, 1 = showing Continue button, 2 = Loading
      doBatchAssist,
      destinations: [],
      originalDestinations: [], // used to compare notes that may have updated (via multibot)
      phase: 'select',
      showPinCode: false,
      showErrorScreen: false,
      codeGivenTo: {},
      codeRefreshed: false,
      showAllDrawersLoaded: false,
      ccTaskInfo
    };

    this.onClickCancelButton = this.onClickCancelButton.bind(this);
    this.onClickDeliverOrder = this.onClickDeliverOrder.bind(this);
    this.finishProcessingAndCloseModal = this.finishProcessingAndCloseModal.bind(this);
    this.getDrawersLoadedForCurrentTask = this.getDrawersLoadedForCurrentTask.bind(this);
    this.onClickCancelPendingUnload = this.onClickCancelPendingUnload.bind(this);

    this.toggleTubeSelected = this.toggleTubeSelected.bind(this);
  }

  /**
   * Toggles if our tube is selected or not
   *
   * @param {string} tubeId - which tube we are updating
   */
  toggleTubeSelected(tubeId) {
    const destinationId = this.props.taskAttrs.waypointB;
    const taskId = this.props.taskAttrs.uuid;

    // Note that this sends a request rather than updating local state immediately.
    const toggleError = this.state.locktubes.requestTubeSelectionToggle(tubeId, destinationId, taskId);
    if (toggleError) {
      console.log("Can't currently toggle " + tubeId + ': ' + toggleError);
    }
  }

  batchToggleTubeSelected = (tubeId, destination) => {
    const destinationId = destination ? destination.name : '';
    const taskId = destination ? destination.taskId : '';

    // Note that this sends a request rather than updating local state immediately.
    const toggleError = this.state.locktubes.requestTubeSelectionToggle(tubeId, destinationId, taskId);
    if (toggleError) {
      console.log("Can't currently toggle " + tubeId + ': ' + toggleError);
    }
  };

  componentDidMount() {
    elasticLogEvent({ what: 'loadScreenShown', taskUuid: this.props.taskAttrs.uuid });

    let ccMode = this.state.ccTaskInfo?.mode;
    if (!this.props.taskAttrs.test && !this.state.doBatchAssist && (ccMode != 'delivery' && ccMode != 'unload')) {
      this.props.chime();
    }

    if (getRobotId()) {
      //... i think we're guaranteed getRobotId() to be truthy bc otherwise no LoadScreen...
      this.rmvLocktubeListener = Locktube.onUpdate(getRobotId(), this.onLocktubeUpdate);
      this.increasePollSpeedInterval = setInterval(() => tmpIncreasePollSpeed(), 500);
    }

    if (getConfig().userFacingBatching) this.updateDestinations();
  }

  componentDidUpdate(prevProps) {
    if (getConfig().userFacingBatching && this.destinationsChanged(prevProps)) {
      this.updateDestinations();
    }
  }

  componentWillUnmount() {
    this.stopLocktubeListener();
  }

  destinationsChanged({ tasks, taskAttrs }) {
    const { destinations } = this.state;
    const batchId = taskAttrs?.batchData?.id;
    let changed = false;

    if (batchId) {
      const batchTasks = tasks.filter((t) => t.task_data.batch?.id === batchId);

      if (batchTasks.length !== destinations.length) changed = true;
      if (batchTasks.length) {
        for (let i = 0; i < batchTasks.length; i++) {
          const task = batchTasks[i];
          const destination = destinations.find((d) => d.taskId === task.uuid);

          if (destination) {
            if (
              destination.id !== task.task_data.batch.index ||
              destination.name !== task.task_level_args.end_goal.value
            ) {
              changed = true;
              break;
            }
          }
          else {
            changed = true;
            break;
          }
        }
      }
    }
    return changed;
  }

  getDrawer(taskId) {
    const { destinations } = this.state;
    const destination = destinations.find((d) => d.taskId === taskId);
    if (!destination) return '';
    return destination.drawer;
  }

  getDrawerName(taskId) {
    const { tubes } = this.state.locktubes;
    let drawer;

    Object.keys(tubes).forEach((t) => {
      if (tubes[t].reservedBy === taskId) drawer = t;
    });

    return drawer;
  }

  getDrawerNamesByTaskId(taskId) {
    const { tubes } = this.state.locktubes;
    let drawers = [];

    Object.keys(tubes).forEach((t) => {
      if (tubes[t].reservedBy === taskId) drawers.push(capitalizeString(t));
    });

    return joinCommaSeparatedNames(drawers);
  }

  onLocktubeUpdate = async (locktubes) => {
    let tubeIds = getLocktubeIds();
    let allTubes = tubeIds.map((tubeId) => locktubes.tubes[tubeId]);
    let isAnyTubeUnlocked = allTubes.some((t) => (t.disabled ? false : t.unlocked)); // Disabled drawers can be unlocked
    let isAnyTubeFilled = allTubes.some((t) => t.filled);
    const isAnyTubeRequested = allTubes.some((t) => t.accessStatus === 'requested');
    const allTubesFilled = allTubes.every((t) => t.filled);
    const taskNotLoaded = allTubes.every((t) => t.reservedBy !== this.props.taskAttrs.uuid);

    let state = {
      locktubes,
      tubesUnlocked: isAnyTubeUnlocked,
      tubesFilled: isAnyTubeFilled, //idea: could consider requiring it be filled for this task's dropoff (vs any dropoff)...
      tubesRequested: isAnyTubeRequested,
      showAllDrawersLoaded: allTubesFilled && taskNotLoaded,
    };

    if (this.min1MDrawers && !this.vanillaStateChecked) {
      //If any drawers aren't in a vanilla state, send a log noting it.
      let nonVanillaDrawers = [];
      for (let drawerName of Object.keys(this.state.locktubes.tubes)) {
        let { filled, destination } = this.state.locktubes.tubes[drawerName];
        if (filled || destination) nonVanillaDrawers.push(drawerName);
      }
      if (nonVanillaDrawers.length) {
        let msg = `some drawers are filled or have destinations: ${nonVanillaDrawers.join(', ')}`;
        elasticLogError({ what: '1MNonVanilla', taskId: this.props.taskAttrs.uuid, msg });
      }
      this.vanillaStateChecked = true;
    }

    let dbInfo = '-----';
    if (locktubes.arrivalReadyForContinue && this.state.arrivalMode == 0) {
      if (state.arrivalMode != 1) dbInfo = 'arrv=>1';
      state.arrivalMode = 1;
    }
    if (locktubes.arrivalDismissed == this.props.taskAttrs.uuid) {
      if (state.arrivalMode != 2) dbInfo = 'arrv=>2';
      state.arrivalMode = 2;
    }

    elasticLogEvent({
      what: 'loadTubeCB',
      cont: !!locktubes.arrivalReadyForContinue,
      dism: locktubes.arrivalDismissed,
      mode1: this.state.arrivalMode,
      mode2: state.arrivalMode,
      dbInfo,
    });

    this.setState(state);

    await this.skipNonFirstLoadIf1M(locktubes.status);

    await this.ccTaskSkipLoadAndFillDrawers(locktubes.status);
  };

  onChangeNoteInput = (value) => this.setState({ currentDeliveryNote: value });

  finishProcessingAndCloseModal(success) {
    this.stopLocktubeListener();
    if(!this.state.waitingForUnload){
      this.setState({ loaderVisible: false, isLoading: false });
    }
    this.props.closeModal(!success);
  }

  async taskCancelFromLoadScreen() {

    await Locktube.taskCancelFromLoadScreen(getRobotId()).catch((error) => {
      elasticLogError({
        what: 'taskCancelFromLoadScreenError',
        taskId: this.props.taskAttrs?.uuid,
        msg: `taskCancelFromLoadScreen: error from Locktube.taskCancelFromLoadScreen: ` + error,
      });
    });
  }


  getDrawersLoadedForCurrentTask(){
    const curTaskUuid = this.props.taskAttrs.uuid;
    const allThreeDrawers = Object.keys(this.state.locktubes.tubes);  // large, medium, small
    let loadedForCurTask = allThreeDrawers.filter((drawerName) => (this.state.locktubes.tubes[drawerName].filled && this.state.locktubes.tubes[drawerName].reservedBy === curTaskUuid));
    return loadedForCurTask;
  }


  async onClickCancelPendingUnload(){
    // tell robot to pop all the drawers loaded for this task and not leave until they're unloaded
    this.setState( { waitingForUnload: true } );

    const curTaskUuid = this.props.taskAttrs.uuid;
    elasticLogTaskAction({ what: 'taskCancelAndUnload', msg:`Task cancelled from load screen, user required to unload drawers for task ID ${curTaskUuid}`, taskUuid: curTaskUuid, context: 'loadscreen'});

    await this.taskCancelFromLoadScreen();  // send info to Moxi
  }


  //TODO: LMR consider using local cancel here when possible, i.e.
  //when this task is on the bot.
  async onClickCancelButton() {

    noteInteraction();
    this.setState({ loaderVisible: true, isLoading: true });
    const curTaskUuid = this.props.taskAttrs.uuid;

    let curTask = this.props.tasks.find(t => t.uuid == curTaskUuid);

    //if this is the load of the salient task of a M:1, just skip it, rather than canceling.
    let batch = curTask.task_data.batch;
    if(
      this.props.autoPrioConfig?.batchTaskCleanCancels &&   //config is enabled, and

      batch?.batchType == 'manyToOne' &&  //this is a M:1 batch, and
      batch.index == batch.total - 1 &&   //this is the last task in the batch, and
      batch.total > 1                     //this isn't the only task in the batch
    ) {
      //mark the bot as loaded so it continues on to the delivery (of prior pickups)
      await Locktube.updateLocktubeStatus(getRobotId(), LOCKTUBE.loaded);
      this.finishProcessingAndCloseModal(!errOccurred);
      return;
    }

    await Locktube.updateLocktubeStatus(getRobotId(), LOCKTUBE.unloaded);


    let allUuids = [curTaskUuid];
    let restricted, is1MLoad, mfa; //leave undefined if task not found
    if (curTask) {
      restricted = !!curTask.task_data.restricted;
      mfa = !!curTask.task_data.code_required;

      //if was load task for 1:M batch, gather info to cancel all batch tasks
      let batch = curTask.task_data.batch;
      if(batch?.batchType == 'oneToMany' && batch.index == 0) {
        is1MLoad = true;
        let batchTasks = this.props.tasks.filter(t => t.task_data?.batch?.id == batch.id);
        batchTasks = batchTasks.filter(t => t.status == 'request' || t.status == 'todo');
        allUuids = [curTaskUuid, ...batchTasks.map(t => t.uuid)];
      }
    }

    elasticLogTaskAction({ what: 'taskCancelled', msg:`Cancel from loadscreen of ${is1MLoad ? '1M load ' : ''}${curTaskUuid}`, taskUuid: curTaskUuid, restricted, mfa, context: 'loadscreen', is1MLoad, allUuids });

    let errOccurred;

    if(this.props.autoPrioConfig?.batchTaskCleanCancels) {
      let result = await TaskRequest.mbotCancelTasks([curTaskUuid]);
      if(result?.error) {
        elasticLogError({ what: 'taskCancelErr', msg:`Error canceling ${curTaskUuid}: ` + result.error, 
          taskUuid: curTaskUuid, restricted, mfa, context: 'loadscreen' });
        errOccurred = true;
      }
    }
    else {
      if(!getUseFirestore()) {
        let result = await TaskRequest.mbotCancelTasks(allUuids);
        if(result.error) {
          elasticLogError({ what: 'taskCancelErrViaMbot', msg:`Error canceling: ` + result.error, allUuids, restricted, mfa, context: 'loadscreen' });
          errOccurred = true;
        }
      }
      else {
        for(let uuid of allUuids) {
          try {
            await TaskRequest.cancel(uuid);
          }
          catch(err) {
            elasticLogError({ what: 'taskCancelErr', msg:`Error canceling ${uuid}: ` + err, taskUuid: uuid, restricted, mfa, context: 'loadscreen' });
            errOccurred = true;
            break;
          }
        }
      }
    }

    this.finishProcessingAndCloseModal(!errOccurred);
  }


  stopLocktubeListener() {
    if (this.rmvLocktubeListener) {
      this.rmvLocktubeListener();
      this.rmvLocktubeListener = undefined;
    }
    if (this.increasePollSpeedInterval) {
      clearInterval(this.increasePollSpeedInterval);
      this.increasePollSpeedInterval = undefined;
    }
  }

  //Used only for dev/test.
  _makeFake1MDrawerMapForTest() {
    if (!this.min1MDrawers) return;
    if (this.props.taskAttrs.batchData.index) return;
    let { id } = this.props.taskAttrs.batchData;
    let allBatchTasks = this.props.tasks.filter((t) => (t.task_data.batch || {}).id == id);
    let stops = allBatchTasks.map((t) => t.task_level_args.end_goal.value);

    if (stops.length < 2 || stops.length > 3) {
      debugger;
      return;
    }

    const allThreeDrawers = Object.keys(this.state.locktubes.tubes);
    let loadedDrawers = allThreeDrawers.filter((drawerName) => this.state.locktubes.tubes[drawerName].filled);
    let drawerMap = {};
    loadedDrawers.forEach((drawerName, i) => (drawerMap[drawerName] = stops[i % stops.length]));
    return drawerMap;
  }

  /*
    _check1MDrawers should only be called for 1:M batches.

    It will return undefined if there are no errors, or a string if there is an error.
    Note than errors returned by this routine would represent bugs rather than 
    bad user interactions.

    Param 'drawerMap1M' is a dictionary mapping drawers (any subset of [small, medium, large])
    to destinations (station codes, *not* friendly names).  This map should include at 
    least one drawer mapping for every dropoff in the 1:M batch.
  */
  _check1MDrawers(drawerMap1M) {
    const makeErr = (msg) => {
      elasticLogError({ what: 'check1MErr', taskId: this.props.taskAttrs.uuid, msg });
      return `check1MDrawers: ${msg}\n(map=${JSON.stringify(drawerMap1M)})`;
    };

    if (!drawerMap1M) return makeErr('drawerMap1M param must be truthy');
    if (!this.min1MDrawers) return makeErr(`Must only be called for 1:M batches.`);
    let mappedDrawerNames = Object.keys(drawerMap1M);
    if (mappedDrawerNames.length > 3) return makeErr(`A max of 3 drawers can be specified`);
    if (mappedDrawerNames.length < 2) return makeErr(`A minimum of 2 drawers must be specified`); // <2 should be 1to1/normal task

    const allThreeDrawers = Object.keys(this.state.locktubes.tubes);
    let unknownDrawers = mappedDrawerNames.filter((d) => allThreeDrawers.indexOf(d) < 0);
    if (unknownDrawers.length) return makeErr(`Unrecognized: ${unknownDrawers.join(', ')}`);

    let curTask = this.props.tasks.find((t) => t.uuid == this.props.taskAttrs.uuid);
    if (!curTask) return makeErr('Unable to find task record for ' + this.props.taskAttrs.uuid);
    if (!curTask.task_data.batch) return makeErr('Current task is not a batch');
    let { id, batchType, index, total, uuids, stops } = curTask.task_data.batch;
    if (batchType != 'oneToMany') return makeErr('Current batch type is not oneToMany');

    //Verify all drawerMap1M stops are on the route
    let mappedStops = mappedDrawerNames.map((d) => drawerMap1M[d]);
    let stopsNotOnRoute = mappedStops.filter((stop) => stops.indexOf(stop) < 0);
    if (stopsNotOnRoute.length) return makeErr(`Some drawer stops are not on the route: ${stopsNotOnRoute.join(', ')}`);

    //Verify that all non-first stops on the route have at least one drawer mapped to them
    let nonRepresentedStops = stops.slice(1).filter((stop) => mappedStops.indexOf(stop) < 0);
    if (nonRepresentedStops.length)
      return makeErr(`Some task stops aren't mapped to drawers: ${nonRepresentedStops.join(', ')}`);

    //Verify that the drawers marked as loaded match the drawers specified in the map.
    // A mismatch might happen in a two-stop 1:M where a nurse changed their mind about a given
    // drawer and the UI didn't retain knowledge of said drawer in the supplied drawerMap1M.
    let loadedDrawers = allThreeDrawers.filter((drawerName) => this.state.locktubes.tubes[drawerName].filled);
    loadedDrawers.sort();
    let mappedDrawers = Object.keys(drawerMap1M);
    mappedDrawers.sort();
    if (!areSame(loadedDrawers, mappedDrawers))
      return makeErr(
        `Mapped drawers (${mappedDrawers.join(', ')}) don't match loaded drawers(${loadedDrawers.join(', ')}).`,
      );
  }

  /*
    set1MDrawers should be called only for tasks in 1:M batches.
    This routine is envisioned to be called once for a given 1:M Load.
    See _check1MDrawers() for a description of the drawerMap1M parameter.

    This routine returns undefined in the event of success, or a string in 
    the case of an error.  This routine does not surface errors to users; 
    that is left to calling code.

    On success, this routine will 
    * set the specified drawer assignments in the ContainerManager.
    * do a reasonable-effort attempt to write the mapping to the firestore task record, for 
        consumption by auto-logging and/or forensics.
  */
  async set1MDrawers(drawerMap1M) {
    // Not checking for errors at this time due to need to support > 3 stops
    // let checkErr = this._check1MDrawers(drawerMap1M, true);
    // if (checkErr) return checkErr;

    elasticLogEvent({
      what: '1MDrawers',
      taskId: this.props.taskAttrs.uuid,
      drawerMap1M,
      msg: `set1MDrawers: ${JSON.stringify(drawerMap1M)}`,
    });

    //set assignments in ContainerManager
    let delta = {};
    Object.keys(drawerMap1M).forEach(
      (drawerName) => (delta[[drawerName, 'destination'].join('.')] = drawerMap1M[drawerName]),
    );
    await Locktube._updateLocktubeDoc(getRobotId(), delta);

    //async attempt to decorate firestore task record
   updateTaskData(this.props.taskAttrs.uuid, { drawerMap1M });
  }

  // TODO: Enable/disable this action (and related button) based on locktube state
  // TODO: If we click deliver while a tube is in the requested state we should probably reset it to available/filled
  /*
    drawerMap1M should be truthy for 1:M tasks, falsey otherwise. See _check1MDrawers() for a more 
    precise description of the drawerMap1M parameter.
  */
  async onClickDeliverOrder() {
    noteInteraction();
    let drawerMap1M;
    // drawerMap1M = drawerMap1M || this._makeFake1MDrawerMapForTest();  //can be uncommented for dev

    if (this.showBatchLoad()) {
      drawerMap1M = {};
      this.state.destinations.forEach((d) => {
        drawerMap1M[d.drawer] = d.name;
      });
    }

    if (drawerMap1M) {
      let assignErrStr = await this.set1MDrawers(drawerMap1M);
      if (assignErrStr) {
        alert('Error: ' + assignErrStr);
        return; //prevent the delivery confirmation
      }
    }

    try {
      this.stopLocktubeListener(); //stop data flowing to the ui we're disabling

      this.setState({
        confirmVisible: false,
        loaderVisible: true,
        isLoading: true,
      });

      let data = {};

      // if note was edited, update it
      if (this.showBatchLoad()) {
        this.state.destinations.forEach((d) => {
          data[SENDER_NOTE] = (d.note || '').trim() + '\n';
          updateTaskData(d.taskId, data);
        });
      }
      else {
        let note = this.getNote(true);
        if (this.props.taskAttrs.batchData) note = note + '\n';
        data[SENDER_NOTE] = note;
        updateTaskData(this.props.taskAttrs.uuid, data);
      }

      const { autoPrioConfig, taskAttrs, tasks } = this.props;

      // If multi-factor, update "given to" data on task(s)
      if (autoPrioConfig?.pin_code_phase1 && taskAttrs.codeRequired) {
        let tasksToUpdate = [];
        if (taskAttrs.batchData && taskAttrs.batchData.type === '1_M') {
          tasksToUpdate = tasks.filter((t) => t.task_data.batch?.id === taskAttrs.batchData.id);
        }
        else {
          const task = tasks.find((t) => t.uuid === taskAttrs.uuid);
          tasksToUpdate.push(task);
        }
        for (let i = 0; i < tasksToUpdate.length; i++) {
          const drawer = this.getDrawerName(tasksToUpdate[i].uuid);
          if (drawer) {
            const givenTo = this.state.codeGivenTo[drawer].value;
            updateTaskData(tasksToUpdate[i].uuid, { code_given_to: givenTo });    //await
          }
        }
      }

      // change locktube status to loaded such that the task executor moves on
      let success = false;
      try {
        let delta = { status: LOCKTUBE.loaded };
        this.addM1DrawersToDelta(delta);
        this.ccLoadRemoveDestinations(delta);
        await Locktube._updateLocktubeDoc(getRobotId(), delta);
        success = true;
      }
      catch (error) {
        elasticLogError({ what: 'updateLocktubeStatus to loaded', errorStr: '' + error });
      }

      this.finishProcessingAndCloseModal(success);
    }
    catch (error) {
      elasticLogError({ what: 'updateLocktubeStatus: exception', errorStr: '' + error });
    }
  }


  //for a cc delivery or unload, marks all drawers as filled and skips this load phase.
  async ccTaskSkipLoadAndFillDrawers(status) {
    let {ccTaskInfo} = this.state;
    if(!ccTaskInfo) return;

    let mode = ccTaskInfo.mode;
    if(mode != 'delivery' && mode != 'unload') return;

    //mark all drawers as for the destination, and skip this load
    elasticLogEvent({
      what: 'skipLoad',
      taskId: this.props.taskAttrs.uuid,
      msg: `ccTaskSkipLoadAndFillDrawers: handling ${mode} ${this.props.taskAttrs.uuid} locktubeStats=${status}`,
    });

    let delta = { status: LOCKTUBE.loaded };
    Object.keys(this.state.locktubes.tubes).forEach((drawerName) => {
      // let { filled, destination, reservedBy } = this.state.locktubes.tubes[drawerName];
      delta[[drawerName, 'reservedBy'].join('.')] = this.props.taskAttrs.uuid;
      delta[[drawerName, 'access_status'].join('.')] = LOCKTUBE_ACCESS_STATUS.filled;
      delta[[drawerName, 'destination'].join('.')] = ccTaskInfo.dropoff;
    });
    await Locktube._updateLocktubeDoc(getRobotId(), delta);
  }


  async skipNonFirstLoadIf1M(status) {
    if (!this.min1MDrawers) return;
    let { index } = this.props.taskAttrs.batchData;
    if (index == 0) return; //we don't skip the first load of a 1:M
    elasticLogEvent({
      what: 'skipLoad',
      taskId: this.props.taskAttrs.uuid,
      msg: `drawerMagic: skipping non-first Load of 1:M task ${this.props.taskAttrs.uuid} locktubeStats=${status}`,
    });
    await Locktube._updateLocktubeDoc(getRobotId(), { status: LOCKTUBE.loaded });
  }


  //this gets called at the end of the load. For a cc load, it clears the
  //destination field of all drawers, so that the ensuing "unload" phase
  //just skips because there are no drawers marked for the destination.
  ccLoadRemoveDestinations(delta) {
    let {ccTaskInfo} = this.state;
    if(!ccTaskInfo) return;

    let mode = ccTaskInfo.mode;
    if(mode != 'load') return;

    Object.keys(this.state.locktubes.tubes).forEach((drawerName) => {
      // let { filled, destination, reservedBy } = this.state.locktubes.tubes[drawerName];
      delta[[drawerName, 'destination'].join('.')] = '';
    });
  }



  addM1DrawersToDelta(delta) {
    let curTask = this.props.tasks.find((t) => t.uuid == this.props.taskAttrs.uuid);
    if (!curTask) throw new Error('Unable to find task record for ' + this.props.taskAttrs.uuid); //todo get this throw out of here
    if (!curTask.task_data.batch) return;
    let { id, batchType, index, total, uuids, stops } = curTask.task_data.batch;
    if (batchType != 'manyToOne') return;
    if (index >= total - 1) return; //- 1 because no switch needed for last pickup, bc it's the same task as dropoff

    let m1EndGoal = stops[stops.length - 1];

    //for any drawers destined for the current task's end_goal, redirect to EndGoalM1
    let markedDrawers = [];
    const curTaskEndGoal = this.props.taskAttrs.waypointB;
    const curTaskId = this.props.taskAttrs.uuid;
    Object.keys(this.state.locktubes.tubes).forEach((drawerName) => {
      let { filled, destination, reservedBy } = this.state.locktubes.tubes[drawerName];
      if (filled && reservedBy == curTaskId && destination == curTaskEndGoal) {
        delta[[drawerName, 'destination'].join('.')] = m1EndGoal;
        markedDrawers.push(drawerName);
      }
    });
    elasticLogEvent({
      what: 'drawersMarked',
      taskId: this.props.taskAttrs.uuid,
      msg: `Marked drawers for ${m1EndGoal}: ${markedDrawers.join(', ') || '(none)'}`,
    });
  }

  closeLoader = () => this.setState({ loaderVisible: false });

  getRequester() {
    return r2h(this.props.taskAttrs.requestedBy);
  }

  getPickupDisplayNames() {
    const pickupWaypoints = this.props.taskAttrs.batchData
      ? this.props.taskAttrs.batchData.pickups
      : [this.props.taskAttrs.waypointA];
    return pickupWaypoints.map((waypt) => r2h(waypt));
  }

  getDropoffDisplayNames() {
    const dropoffWaypoints = this.props.taskAttrs.batchData
      ? this.props.taskAttrs.batchData.dropoffs
      : [this.props.taskAttrs.waypointB];
    return dropoffWaypoints.map((waypt) => r2h(waypt));
  }

  getCurrentStopIndex() {
    if (this.props.taskAttrs.batchData) {
      return this.props.taskAttrs.batchData.index;
    }
    else {
      return 0; // Always pickup since this is load screen
    }
  }

  getLoadInstructions() {
    return this.state.locktubes.anyTubeUnlocked() ? LOAD_PROMPTS.closeStorage : LOAD_PROMPTS.selectStorage;
  }

  async signalArrivalTapped() {
    try {
      elasticLogEvent({ what: 'screenTapped' });
      await Locktube.signalArrivalTapped(getRobotId());
    }
    catch (err) {
      elasticLogError({ what: 'tapError', msg: 'signalArrivalTapped error: ' + err });
    }
  }

  updateDestinations() {
    const { taskAttrs, tasks } = this.props;

    if (taskAttrs.batchData?.type === BatchTypes.oneToMany) {
      const batchId = taskAttrs.batchData.id;
      const batchTasks = tasks.filter((t) => t.task_data.batch?.id === batchId);

      if (batchTasks) {
        batchTasks.sort((a, b) => a.task_data.batch.index - b.task_data.batch.index);
        const destinationMap = batchTasks.map((t) => ({
          id: t.task_data.batch.index,
          taskId: t.uuid,
          name: t.task_level_args.end_goal.value,
          note: t.task_data.sender_note.trim(),
          drawer: this.getDrawer(t.uuid),
        }));
        this.setState({ destinations: destinationMap, originalDestinations: destinationMap });
      }
    }
  }

  getNote(isInput = false) {
    const { currentDeliveryNote, codeGivenTo, destinations } = this.state;
    const { autoPrioConfig, taskAttrs } = this.props;

    let note;
    if (this.showBatchLoad()) {
      const combinedNotes = destinations.map((d) => {
        note = d.note ? d.note.trim() : '';
        if (autoPrioConfig?.pin_code_phase1 && taskAttrs.codeRequired && Object.keys(codeGivenTo).length && !isInput) {
          const givenTo = codeGivenTo[d.drawer].value;
          if (note.length && !note.endsWith('.')) note += '.';
          note += ` Code given to ${givenTo}`;
        }
        else if (!note) {
          note = <span className="no-notes">No notes</span>;
        }

        return (
          <div className="details__multi-note" key={`note-${d.id}`}>
            <strong>{r2h(d.name)}: </strong>
            {note}
          </div>
        );
      });
      return <>{combinedNotes}</>;
    }

    note = currentDeliveryNote !== null ? currentDeliveryNote : taskAttrs.note.trim();
    if (autoPrioConfig?.pin_code_phase1 && taskAttrs.codeRequired && Object.keys(codeGivenTo).length && !isInput) {
      const drawer = this.getDrawerName(taskAttrs.uuid);
      if (note.length && !note.endsWith('.')) note += '.';
      note += `${note.length ? ' ' : ''}Code given to ${codeGivenTo[drawer].value}`;
    }
    return note;
  }

  getDrawers() {
    if (this.showBatchLoad()) {
      return capitalizeStringArray(this.state.destinations.map((d) => d.drawer));
    }
    return capitalizeStringArray(getFilledStorageNames(this.state.locktubes));
  }

  showBatchLoad() {
    return (
      getConfig().userFacingBatching &&
      this.props.taskAttrs.batchData?.type === BatchTypes.oneToMany &&
      this.state.destinations.length <= 3
    );
  }

  async handleContinue() {
    this.setState({ showErrorScreen: false });

    if (this.state.showAllDrawersLoaded) {
      this.setState({ confirmVisible: true, showAllDrawersLoaded: false });
    }
    else if (this.props.autoPrioConfig?.pin_code_phase1 && this.props.taskAttrs.codeRequired) {
      // Generate pin code(s)

      let tasks = [];
      if (this.props.taskAttrs.batchData && this.props.taskAttrs.batchData.type === '1_M') {
        tasks = this.props.tasks.filter((t) => t.task_data.batch?.id === this.props.taskAttrs.batchData.id);
      }
      else {
        const task = this.props.tasks.find((t) => t.uuid === this.props.taskAttrs.uuid);
        tasks.push(task);
      }
      const pinCodes = await generatePinCodes(tasks.map((t) => t.uuid));

      const givenToState = {};
      tasks.forEach((t) => {
        const drawer = this.getDrawerName(t.uuid);
        if (drawer) {
          givenToState[drawer] = { value: '', error: '' };
        }
      });

      if (Object.keys(pinCodes).length) {
        this.setState({ showPinCode: true, pinCodes, codeGivenTo: givenToState });
      }
      else {
        this.setState({ showErrorScreen: true, codeGivenTo: givenToState });
      }
    }
    else {
      this.setState({ confirmVisible: true });
    }
  }

  getPinCodeData() {
    const { destinations, currentDeliveryNote, pinCodes, codeGivenTo } = this.state;
    const { autoPrioConfig, taskAttrs, tasks } = this.props;
    const data = [];

    if (autoPrioConfig?.pin_code_phase1 & taskAttrs.codeRequired) {
      let pinCodeTasks = [];

      if (taskAttrs.batchData && taskAttrs.batchData.type === '1_M') {
        pinCodeTasks = tasks.filter((t) => t.task_data.batch?.id === taskAttrs.batchData.id);
      }
      else {
        const task = tasks.find((t) => t.uuid === taskAttrs.uuid);
        pinCodeTasks.push(task);
      }
      pinCodeTasks.forEach((t) => {
        const drawer = this.getDrawerName(t.uuid);

        if (drawer) {
          let note;
          if (this.showBatchLoad()) {
            const destination = destinations.find((d) => d.drawer === drawer);
            note = destination?.note || '';
          }
          else {
            note = currentDeliveryNote !== null ? currentDeliveryNote : taskAttrs.note.trim();
          }

          let destination = t.task_level_args.end_goal.value;

          if (taskAttrs.batchData) {
            if (taskAttrs.batchData.type === 'M_1') destination = taskAttrs.batchData.dropoffs[0];
          }

          data.push({
            id: drawer,
            drawer: this.getDrawerNamesByTaskId(t.uuid),
            name: r2h(destination),
            note,
            pinCode: pinCodes ? pinCodes[t.uuid] : '',
            codeGivenTo: codeGivenTo[drawer],
          });
        }
      });
    }
    else {
      return null;
    }

    return data;
  }

  getContinueButtonText = () => {
    const { autoPrioConfig, taskAttrs } = this.props;
    if (autoPrioConfig?.pin_code_phase1 && taskAttrs.codeRequired) {
      if (this.getPinCodeData().length > 0) {
        return taskAttrs.batchData?.type === '1_M' ? 'GET CODES' : 'GET CODE';
      }
      return 'CONTINUE';
    }
    return 'DELIVER';
  };

  render() {
    let ccMode = this.state.ccTaskInfo?.mode;
    if (this.state.doBatchAssist || (ccMode && ccMode != 'load')) {
      //don't show nurse interactivity; wait for drawerMagic to get things past this state
      return <ThinkingScreen />;
    }

    if (!this.props.taskAttrs.uuid) {
      //can happen if load screen shown when task gets cancelled
      return <div>(No current task)</div>;
    }

    if (this.state.arrivalMode < 2) {

      let mainText = GENERAL_PICKUP_TEXT;
      if(ccMode && ccMode == 'load')
        mainText = `Load-up to start a delivery shift`;
      else if(!this.props.taskAttrs.failedToPullTask)
        mainText = composeArrivalLoadText(this.getDropoffDisplayNames(), this.props.taskAttrs.isScheduled, this.props.taskAttrs.restricted, this.props.taskAttrs.codeRequired );

      return (
        <ArrivalScreen
          mainText={mainText}
          noteText={this.props.taskAttrs.failedToPullTask ? LOST_CONNECTION_TEXT : this.props.taskAttrs.note?.trim()}
          requestedBy={this.getRequester()}
          clickButton={() => this.signalArrivalTapped()}
          showButton={this.state.arrivalMode == 1}
          textDisplayMode={this.props.taskAttrs.failedToPullTask}
          tapToReload={() => countForReload({ label: 'Arrival' })}
          isRestricted={this.props.taskAttrs.restricted}
        />
      );
    }


    // Show error screen if no drawers are available at pickup
    if (this.state.showAllDrawersLoaded) {
      return (
        <ErrorScreen
          buttonText="CONTINUE"
          buttonAction={() => this.handleContinue()}
          subtitle={`You will need to request a new delivery. Contact Moxi support ${
            getConfig().site.supportNumber ? `(${getConfig().site.supportNumber})` : ''
          } to address this issue.`}
          tapToReload={() => countForReload({ label: 'LoadScreen' })}
          title="All of Moxi's drawers have been loaded."
        />
      );
    }

    let screen;
    if(this.state.confirmVisible) {
      screen = <ConfirmLoadScreen
        requester={this.props.taskAttrs.isScheduled ? 'Scheduled' : this.getRequester()}
        note={this.getNote()}
        storageFilled={this.getDrawers()}
        pickups={this.getPickupDisplayNames()}
        dropoffs={this.getDropoffDisplayNames()}
        currentStopIndex={this.getCurrentStopIndex()}
        clickConfirm={this.onClickDeliverOrder}
        clickCancel={() => this.setState({ confirmVisible: false })}
        tapToReload={() => countForReload({ label: 'ConfirmLoad' })}
        hideDetails={this.props.taskAttrs.failedToPullTask || ccMode}
        isRestricted={this.props.taskAttrs.restricted}
        codeRequired={this.props.taskAttrs.codeRequired}
        pinCodeData={this.getPinCodeData()}
      />
    }
    else if(this.state.showPinCode) {
      screen = <PinCodeScreen
        clickCancel={() => {
          this.setState({ confirmVisible: false, showPinCode: false, codeRefreshed: true });
        }}
        clickConfirm={() => this.setState({ confirmVisible: true, codeRefreshed: false })}
        destinations={this.getPinCodeData()}
        tapToReload={() => countForReload({ label: 'PinCodeScreen' })}
        givenTo={this.state.codeGivenTo}
        setGivenTo={(data) => this.setState({ codeGivenTo: data })}
        codeRefreshed={this.state.codeRefreshed}
      />    
    }
    else if(this.state.showErrorScreen ) {
      screen = <ErrorScreen
        buttonText="TRY AGAIN"
        buttonAction={() => this.handleContinue()}
        subtitle={`This screen can't be shown right now. Contact Moxi support ${
          getConfig().site.supportNumber ? `(${getConfig().site.supportNumber})` : ''
        } to resolve this issue and then try again.`}
        tapToReload={() => countForReload({ label: 'LoadScreen' })}
        title="One moment, there may be an issue."
      />
    }
    else if(this.showBatchLoad()) {
      screen = <BatchLoadItemsScreen
        clickCancel={this.onClickCancelButton}
        clickConfirm={() => this.handleContinue()}
        storageState={this.state.locktubes}
        toggleStorageSelection={this.batchToggleTubeSelected}
        requestedBy={this.getRequester()}
        tapToReload={() => countForReload({ label: 'LoadScreen' })}
        hideDetails={this.props.taskAttrs.failedToPullTask}
        isRestricted={this.props.taskAttrs.restricted}
        destinations={this.state.destinations}
        setDestinations={(destinations) => this.setState({ destinations })}
        phase={this.state.phase}
        setPhase={(phase) => this.setState({ phase })}
        taskData={this.props.tasks.filter(t => (t.task_data.batch || {}).id == this.props.taskAttrs.batchData.id)}
        originalDestinations={this.state.originalDestinations}
        continueButtonText={this.getContinueButtonText()}
        codeRequired={this.props.taskAttrs.codeRequired}
        autoPrioConfig={this.props.autoPrioConfig}
        getEnabledKioskFriendlyNames={this.props.getEnabledKioskFriendlyNames}
        getOpenHoursInfo={this.props.getOpenHoursInfo}
        getDrawersLoadedForCurrentTask={this.getDrawersLoadedForCurrentTask}
        onClickCancelPendingUnload={this.onClickCancelPendingUnload}
        taskUuid={this.props.taskAttrs.uuid}
      />
    }
    else {
      screen = <LoadItemsScreen
        clickCancel={this.onClickCancelButton}
        clickConfirm={() => this.handleContinue()}
        confirmDisabled={this.state.tubesUnlocked || !this.state.tubesFilled || this.state.tubesRequested}
        dropoffNames={this.getDropoffDisplayNames()}
        loadInstructions={this.getLoadInstructions()}
        storageState={this.state.locktubes}
        toggleStorageSelection={this.toggleTubeSelected}
        noteText={this.getNote(true)}
        noteOnChange={this.onChangeNoteInput}
        requestedBy={this.getRequester()}
        tapToReload={() => countForReload({ label: 'LoadScreen' })}
        hideDetails={this.props.taskAttrs.failedToPullTask}
        isRestricted={this.props.taskAttrs.restricted}
        continueButtonText={this.getContinueButtonText()}
        codeRequired={this.props.taskAttrs.codeRequired}
        autoPrioConfig={this.props.autoPrioConfig}
        getOpenHoursInfo={this.props.getOpenHoursInfo}
        taskData={this.props.tasks.find(t => t.uuid == this.props.taskAttrs.uuid)}
        destinations={this.state.destinations}
        getEnabledKioskFriendlyNames={this.props.getEnabledKioskFriendlyNames}
        getDrawersLoadedForCurrentTask={this.getDrawersLoadedForCurrentTask}
        onClickCancelPendingUnload={this.onClickCancelPendingUnload}
        taskUuid={this.props.taskAttrs.uuid}
      />
    }


    return (
      <div style={{ ...Styles.container }}>
        {this.state.loaderVisible && <Loader loading={this.state.isLoading} />}
        {screen}
      </div>
    );
  }
}

// Note: This is needed because publishing to expo runs minify, which changes the class names. In particular, classes
//       can end up with the same name which breaks the screen transition logic.
LoadScreen.viewName = 'LoadScreen';
